import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ReadOnlyGuard } from '../permissions/ReadOnlyGuard';
import { CircleDot, MapPin, User, Phone, Mail, FileText, ArrowLeft, Pencil, CheckCircle2, Globe, Truck, ClipboardList, Warehouse, Package, Calculator, Euro, Download, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import { calcTotals, computeSharedValues } from '../../lib/priceFormula';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    nouveau: 'bg-blue-100 text-blue-700 border-blue-200',
    en_redaction: 'bg-orange-100 text-orange-700 border-orange-200',
    termine: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const STATUS_BORDER_COLORS = {
    nouveau: 'border-blue-400',
    en_redaction: 'border-orange-400',
    termine: 'border-emerald-400',
};

const TYPE_COLORS = {
    devis_estimatif: 'bg-violet-100 text-violet-700',
    listing_email: 'bg-indigo-100 text-indigo-700',
    listing_telephone: 'bg-violet-100 text-violet-700',
    visite_virtuelle: 'bg-cyan-100 text-cyan-700',
    visite_presentielle: 'bg-teal-100 text-teal-700',
};

export function InventaireDetailContent({ inventaire, config, onClose, onEdit, onRefresh }) {
    const navigate = useNavigate();
    const [articlesData, setArticlesData] = useState({ articles: [], volume_total: 0 });
    const [programmationStats, setProgrammationStats] = useState({});
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    // Formule HT + TVA paramétrées dans Admin > Paramètres (source unique de vérité,
    // partagée avec PriceCalculator et le PDF backend).
    const [priceSettings, setPriceSettings] = useState(null);

    useEffect(() => {
        axios.get(`${API}/api/admin/price-settings`, { withCredentials: true })
            .then(res => setPriceSettings(res.data))
            .catch(() => setPriceSettings(null));
    }, []);

    useEffect(() => {
        if (inventaire?.id) {
            axios.get(`${API}/api/inventaires/${inventaire.id}/articles`, { withCredentials: true })
                .then(res => setArticlesData(res.data))
                .catch(() => {});
            axios.get(`${API}/api/inventaires/${inventaire.id}/programmation`, { withCredentials: true })
                .then(res => setProgrammationStats(res.data || {}))
                .catch(() => {});
        }
    }, [inventaire?.id]);

    // Fetch PDF as blob for preview
    useEffect(() => {
        let revokeUrl = null;
        if (pdfPreviewOpen && inventaire?.id) {
            axios.get(`${API}/api/inventaires/${inventaire.id}/pdf`, { withCredentials: true, responseType: 'blob' })
                .then(res => {
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    revokeUrl = url;
                    setPdfUrl(url);
                    setPdfBlob(blob);
                })
                .catch(() => toast.error('Erreur lors du chargement du PDF'));
        } else {
            setPdfUrl(null);
            setPdfBlob(null);
        }
        return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
    }, [pdfPreviewOpen, inventaire?.id]);

    if (!inventaire) return null;

    const getTypeLabel = (t) => config?.types?.find(x => x.value === t)?.label || t;
    const getStatusLabel = (s) => config?.statuses?.find(x => x.value === s)?.label || s;
    const getSourceLabel = (s) => config?.sources?.find(x => x.value === s)?.label || s || '-';
    const getDeptLabel = (c) => config?.departements?.find(d => d.code === c)?.label || c;
    const getAscenseurLabel = (v) => config?.ascenseur_options?.find(a => a.value === v)?.label || v || '-';

    const isSectionComplete = {
        infos: () => !!inventaire.client_nom && !!inventaire.telephone && !!inventaire.email,
        source: () => !!inventaire.source && !!inventaire.departement,
        adresseDepart: () => {
            const a = inventaire.adresse_depart;
            return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        adresseArrivee: () => {
            const a = inventaire.adresse_arrivee;
            return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        remarques: () => !!inventaire.remarques,
        typeCommercial: () => !!inventaire.type_inventaire && !!inventaire.commercial_id,
        formules: () => Array.isArray(inventaire.formules) && inventaire.formules.length > 0,
        gardeMeuble: () => inventaire.garde_meuble === false || (inventaire.garde_meuble === true && !!inventaire.garde_meuble_tarif && !!inventaire.garde_meuble_duree),
        caveGarage: () => inventaire.cave_garage === false || (inventaire.cave_garage === true && !!inventaire.cave_garage_detail),
        remarquesInventaire: () => !!inventaire.remarques_inventaire,
    };

    const allSectionsComplete = (
        isSectionComplete.typeCommercial()
        && isSectionComplete.infos()
        && isSectionComplete.source()
        && isSectionComplete.adresseDepart()
        && isSectionComplete.adresseArrivee()
        && isSectionComplete.formules()
        && isSectionComplete.gardeMeuble()
        && isSectionComplete.caveGarage()
        && isSectionComplete.remarquesInventaire()
    );

    // Total TTC affichable ? On reflete la meme logique que le bloc "10. Calcul
    // de prix" : il faut que la formule Admin existe ET qu'au moins une formule
    // soit complete (tarif demenageur, panier, hotel, marge, tarifs visibles
    // — incluant tarif_dechetterie / tarif_objet_lourd si presence detectee).
    const priceTtcVisible = (() => {
        try {
            const cp = inventaire.calcul_prix || {};
            const fList = (inventaire.formules || []);
            if (!fList.length) return false;
            const formulaStr = priceSettings?.formula_ht || '';
            if (!formulaStr) return false;
            const tvaRate = typeof priceSettings?.tva_rate === 'number' ? priceSettings.tva_rate : 20;
            const fd = cp?.formules_data || {};
            const adD = inventaire.adresse_depart || {};
            const adA = inventaire.adresse_arrivee || {};
            const sharedKeys = [];
            if (adD.autorisation_stationnement === true) sharedKeys.push('prix_aot_depart');
            if (adA.autorisation_stationnement === true) sharedKeys.push('prix_aot_arrivee');
            if (adD.monte_meuble === true) sharedKeys.push('prix_monte_meuble_depart');
            if (adA.monte_meuble === true) sharedKeys.push('prix_monte_meuble_arrivee');
            const _articles = articlesData?.articles || [];
            const presenceDechetterie = _articles.some(a => (a.type_article || '') === 'decheterie' && (parseFloat(a.volume) || 0) > 0);
            const presenceObjetLourd = _articles.some(a => a.objet_lourd === true);
            if (presenceDechetterie) sharedKeys.push('tarif_dechetterie');
            if (presenceObjetLourd) sharedKeys.push('tarif_objet_lourd');
            const sharedValues = computeSharedValues(fd, sharedKeys);
            return fList.some(f => calcTotals(
                fd[f], sharedKeys, cp?.infos_base || {}, cp?.fournitures || {},
                formulaStr, tvaRate, cp?.fournitures_active,
                { presenceDechetterie, presenceObjetLourd, sharedValues },
            ).complete);
        } catch {
            return false;
        }
    })();

    // Conditions globales pour activer les boutons "Telecharger PDF" et "Creer devis"
    // ainsi que pour afficher le statut "Termine" : il faut a la fois que toutes
    // les sections de l'inventaire soient completes ET que le Total TTC s'affiche.
    const allConditionsMet = allSectionsComplete && priceTtcVisible;

    const SectionLine = ({ isComplete }) => (
        <div className={`w-1 rounded-full self-stretch shrink-0 ${isComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
    );

    const SectionCheck = ({ isComplete }) => (
        isComplete ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : null
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try { return format(new Date(dateStr), 'dd MMMM yyyy', { locale: fr }); } catch { return dateStr; }
    };

    const AddressBlock = ({ title, address, icon: Icon, isComplete }) => {
        if (!address || !address.adresse) {
            return (
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Icon size={16} className="text-[#2e7cd6]" />{title}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={false} />
                            <p className="text-sm text-slate-400 italic">Non renseigné</p>
                        </div>
                    </CardContent>
                </Card>
            );
        }
        return (
            <Card className="border-slate-200">
                <CardHeader className="pb-2 border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                        <Icon size={16} className="text-[#2e7cd6]" />{title}<SectionCheck isComplete={isComplete} />
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                    <div className="flex gap-4">
                        <SectionLine isComplete={isComplete} />
                        <div className="flex-1 divide-y divide-slate-100">
                            {address.date && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Date</p>
                                    <p className="text-sm font-medium text-slate-900">{formatDate(address.date)}</p>
                                </div>
                            )}
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Adresse</p>
                                <p className="text-sm font-medium text-slate-900">{address.adresse}</p>
                            </div>
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Étage</p>
                                <p className="text-sm font-medium text-slate-900">{address.etage || '-'}</p>
                            </div>
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Ascenseur</p>
                                <p className="text-sm font-medium text-slate-900">{getAscenseurLabel(address.ascenseur)}</p>
                            </div>
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Portage</p>
                                <p className="text-sm font-medium text-slate-900">{address.portage || '-'}</p>
                            </div>
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Autorisation stat.</p>
                                <p className="text-sm font-medium text-slate-900">{address.autorisation_stationnement === true ? 'Oui' : address.autorisation_stationnement === false ? 'Non' : '-'}</p>
                            </div>
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Monte-meuble</p>
                                <p className="text-sm font-medium text-slate-900">{address.monte_meuble === true ? 'Oui' : address.monte_meuble === false ? 'Non' : '-'}</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <>
            <ReadOnlyGuard module="inventaires">
            {/* Sticky Header — same as Prospect detail */}
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={onClose} data-testid="inventaire-detail-retour-btn">
                        <ArrowLeft size={16} className="mr-2" />Retour
                    </Button>
                    <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="inventaire-detail-title">
                        Aperçu de l'inventaire
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={onEdit} data-testid="inventaire-detail-edit-btn">
                        <Pencil size={16} className="mr-2" />Modifier
                    </Button>
                    <Button
                        variant="outline"
                        disabled={!allConditionsMet || generatingPdf}
                        className={!allConditionsMet ? 'bg-blue-50 text-blue-300 border-blue-200 hover:bg-blue-50 cursor-not-allowed' : 'bg-white text-[#2e7cd6] border-[#2e7cd6] hover:bg-blue-50'}
                        onClick={async () => {
                            // Ordinateur (≥1024 px) : on garde le modal d'aperçu (l'iframe sait
                            // afficher les PDF). Sur tablette / mobile, l'iframe ne fonctionne
                            // pas → on ouvre le PDF directement dans un nouvel onglet, ce qui
                            // utilise le lecteur PDF natif du navigateur (instantané).
                            const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
                            if (isDesktop) {
                                setPdfPreviewOpen(true);
                                return;
                            }
                            // Tablette / mobile : pré-ouvre un onglet (sinon Safari bloque le
                            // window.open après l'await), puis charge le blob dedans.
                            setGeneratingPdf(true);
                            const newTab = window.open('about:blank', '_blank');
                            try {
                                const res = await axios.get(`${API}/api/inventaires/${inventaire.id}/pdf`, { withCredentials: true, responseType: 'blob' });
                                const blob = new Blob([res.data], { type: 'application/pdf' });
                                const url = URL.createObjectURL(blob);
                                if (newTab) {
                                    newTab.location.href = url;
                                } else {
                                    // Pop-up bloqué : fallback download direct
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `inventaire_${inventaire.client_nom || inventaire.id}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                }
                                // On révoque dans 60 s pour laisser au navigateur le temps de l'ouvrir
                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                            } catch {
                                if (newTab) newTab.close();
                                toast.error('Erreur lors du chargement du PDF');
                            } finally {
                                setGeneratingPdf(false);
                            }
                        }}
                        data-testid="inventaire-detail-download-btn"
                    >
                        {generatingPdf ? (
                            <><div className="w-4 h-4 mr-2 border-2 border-[#2e7cd6] border-t-transparent rounded-full animate-spin" />Génération...</>
                        ) : (
                            <><Download size={16} className="mr-2" />Télécharger PDF</>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={async () => {
                            try {
                                await axios.post(`${API}/api/devis/dossier/${inventaire.id}/creer`, {}, { withCredentials: true });
                                onClose();
                                navigate(`/devis/${inventaire.id}`);
                            } catch (err) {
                                if (err.response?.status === 400 && err.response?.data?.detail?.includes('terminé')) {
                                    toast.error("L'inventaire doit être terminé");
                                } else {
                                    // Dossier already exists, just navigate
                                    onClose();
                                    navigate(`/devis/${inventaire.id}`);
                                }
                            }
                        }}
                        disabled={!allConditionsMet}
                        className={!allConditionsMet ? 'bg-blue-100 text-blue-300 border-blue-200 hover:bg-blue-100 cursor-not-allowed' : 'bg-[#2e7cd6] text-white border-[#2e7cd6] hover:bg-[#2568b5]'}
                        data-testid="inventaire-detail-devis-btn"
                    >
                        <FileText size={16} className="mr-2" />Créer devis
                    </Button>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                <div className="space-y-6">

                    {/* Statut — read-only */}
                    <Card className={`bg-blue-50 border-2 ${STATUS_BORDER_COLORS[inventaire.statut] || 'border-blue-200'}`}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                                <CircleDot size={16} />
                                Statut de l'inventaire
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Badge variant="outline" className={`${STATUS_COLORS[inventaire.statut]} border text-sm px-3 py-1`}>
                                {getStatusLabel(inventaire.statut)}
                            </Badge>
                        </CardContent>
                    </Card>

                    {/* 1. Type et Commercial */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <ClipboardList size={16} className="text-[#2e7cd6]" />1. Type et Commercial <SectionCheck isComplete={isSectionComplete.typeCommercial()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.typeCommercial()} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Type</p>
                                        <div className="flex flex-col gap-1">
                                            <Badge className={TYPE_COLORS[inventaire.type_inventaire] || 'bg-slate-100 text-slate-700'}>{getTypeLabel(inventaire.type_inventaire)}</Badge>
                                            {inventaire.type_inventaire_initial && inventaire.type_inventaire_initial !== inventaire.type_inventaire && (
                                                <span className="text-[11px] text-red-600 italic" data-testid="inventaire-type-modifie-note">
                                                    Modifié — était : {getTypeLabel(inventaire.type_inventaire_initial)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Commercial</p>
                                        {inventaire.commercial_name ? (
                                            <Badge variant="outline" className="border-slate-300">
                                                {inventaire.commercial_couleur && <span className="w-2.5 h-2.5 rounded-full mr-1.5 shrink-0 inline-block" style={{ backgroundColor: inventaire.commercial_couleur }} />}
                                                {inventaire.commercial_name}
                                            </Badge>
                                        ) : <span className="text-sm text-slate-400 italic">Non affecté</span>}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 2. Informations générales */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <User size={16} className="text-[#2e7cd6]" />2. Informations générales <SectionCheck isComplete={isSectionComplete.infos()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.infos()} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Nom</p>
                                        <p className="text-sm font-medium text-slate-900">{inventaire.client_nom || '-'}</p>
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Téléphone</p>
                                        {inventaire.telephone ? (
                                            <p className="flex items-center gap-2 text-sm text-slate-900">
                                                <Phone size={14} className="text-[#2e7cd6]" />{inventaire.telephone}
                                            </p>
                                        ) : <p className="text-slate-400 italic text-sm">Non renseigné</p>}
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Email</p>
                                        {inventaire.email ? (
                                            <p className="flex items-center gap-2 text-sm text-slate-900">
                                                <Mail size={14} className="text-[#2e7cd6]" />{inventaire.email}
                                            </p>
                                        ) : <p className="text-slate-400 italic text-sm">Non renseigné</p>}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 3. Source & Département */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Globe size={16} className="text-[#2e7cd6]" />3. Source & Département <SectionCheck isComplete={isSectionComplete.source()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.source()} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Source</p>
                                        <p className="text-sm font-medium text-slate-900">{getSourceLabel(inventaire.source)}</p>
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Département</p>
                                        <p className="text-sm font-medium text-slate-900">{inventaire.departement ? getDeptLabel(inventaire.departement) : '-'}</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 4. Adresse de départ */}
                    <AddressBlock title="4. Adresse de départ" address={inventaire.adresse_depart} icon={MapPin} isComplete={isSectionComplete.adresseDepart()} />

                    {/* 5. Adresse d'arrivée */}
                    <AddressBlock title="5. Adresse d'arrivée" address={inventaire.adresse_arrivee} icon={Truck} isComplete={isSectionComplete.adresseArrivee()} />

                    {/* 6. Formule */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <ClipboardList size={16} className="text-[#2e7cd6]" />6. Formule <SectionCheck isComplete={inventaire.formules && inventaire.formules.length > 0} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={inventaire.formules && inventaire.formules.length > 0} />
                                <div className="flex-1">
                                    {inventaire.formules && inventaire.formules.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {inventaire.formules.map(f => (
                                                <Badge key={f} className="bg-blue-100 text-blue-700">{config?.formules?.find(x => x.value === f)?.label || f}</Badge>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400 italic">Aucune formule sélectionnée</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 7. Garde-meuble */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Warehouse size={16} className="text-[#2e7cd6]" />7. Garde-meuble <SectionCheck isComplete={inventaire.garde_meuble === false || (inventaire.garde_meuble === true && !!inventaire.garde_meuble_tarif && !!inventaire.garde_meuble_duree)} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={inventaire.garde_meuble === false || (inventaire.garde_meuble === true && !!inventaire.garde_meuble_tarif && !!inventaire.garde_meuble_duree)} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Garde-meuble</p>
                                        <p className="text-sm font-medium text-slate-900">{inventaire.garde_meuble === true ? 'Oui' : inventaire.garde_meuble === false ? 'Non' : <span className="text-slate-400 italic">Non renseigné</span>}</p>
                                    </div>
                                    {inventaire.garde_meuble === true && (
                                        <>
                                            <div className="flex items-center gap-4 py-3">
                                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Mensualité (tarif)</p>
                                                <p className="text-sm font-medium text-slate-900">{inventaire.garde_meuble_tarif || <span className="text-slate-400 italic">-</span>}</p>
                                            </div>
                                            <div className="flex items-center gap-4 py-3">
                                                <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Durée</p>
                                                <p className="text-sm font-medium text-slate-900">{inventaire.garde_meuble_duree || <span className="text-slate-400 italic">-</span>}</p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 8. Cave / Garage */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Warehouse size={16} className="text-[#2e7cd6]" />8. Cave / Garage <SectionCheck isComplete={inventaire.cave_garage === false || (inventaire.cave_garage === true && !!inventaire.cave_garage_detail)} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={inventaire.cave_garage === false || (inventaire.cave_garage === true && !!inventaire.cave_garage_detail)} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Cave / Garage</p>
                                        <p className="text-sm font-medium text-slate-900">{inventaire.cave_garage === true ? 'Oui' : inventaire.cave_garage === false ? 'Non' : <span className="text-slate-400 italic">Non renseigné</span>}</p>
                                    </div>
                                    {inventaire.cave_garage === true && (
                                        <div className="flex items-center gap-4 py-3">
                                            <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Détail</p>
                                            <p className="text-sm font-medium text-slate-900">{inventaire.cave_garage_detail || <span className="text-slate-400 italic">-</span>}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 8. Remarques inventaire */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <FileText size={16} className="text-[#2e7cd6]" />8. Remarques inventaire <SectionCheck isComplete={!!inventaire.remarques_inventaire} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={!!inventaire.remarques_inventaire} />
                                <p className="text-sm text-slate-700 whitespace-pre-wrap min-w-0 break-words">{inventaire.remarques_inventaire || <span className="text-slate-400 italic">Aucune remarque</span>}</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 9. Calculateur de volume */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                    <Package size={16} className="text-[#2e7cd6]" />9. Calculateur de volume
                                </CardTitle>
                                <div className="bg-[#2e7cd6] text-white px-3 py-1 rounded-full text-xs font-bold">
                                    {articlesData.volume_total} m³
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-4">
                            {(() => {
                                const mainArticles = articlesData.articles.filter(a => a.type_article !== 'reste');
                                const resteArticles = articlesData.articles.filter(a => a.type_article === 'reste');
                                const demontageArticles = articlesData.articles.filter(a => a.demontage_remontage);
                                const lourdArticles = articlesData.articles.filter(a => a.objet_lourd);
                                const CARTON_NAMES = ['carton standard', 'carton livre', 'carton fragile', 'carton penderie', 'carton bouteille', 'carton verres'];
                                const TYPE_ORDER = ['dem_1', 'encombrant', 'decheterie', 'dem_2', 'garde_meuble', 'reste'];
                                const TC = {
                                    dem_1:        { label: 'Dém. principal', rowBg: '',                textColor: 'text-black',       barBg: 'bg-white border-slate-300' },
                                    encombrant:   { label: 'Encombrant',    rowBg: 'bg-yellow-100/60', textColor: 'text-yellow-700',  barBg: 'bg-yellow-100 border-yellow-300' },
                                    decheterie:   { label: 'Déchèterie',    rowBg: 'bg-red-50/40',     textColor: 'text-red-600',     barBg: 'bg-red-50 border-red-200' },
                                    dem_2:        { label: 'Dém 2',         rowBg: 'bg-sky-50/40',     textColor: 'text-sky-700',     barBg: 'bg-sky-50 border-sky-200' },
                                    garde_meuble: { label: 'Garde meuble',  rowBg: 'bg-slate-100/50',  textColor: 'text-slate-600',   barBg: 'bg-slate-50 border-slate-200' },
                                    reste:        { label: 'Reste',         rowBg: 'bg-gray-50/40',    textColor: 'text-gray-600',    barBg: 'bg-gray-50 border-gray-200' },
                                };
                                // Sort: by type, cartons first within each type
                                const sorted = [...mainArticles].sort((a, b) => {
                                    const tA = TYPE_ORDER.indexOf(a.type_article), tB = TYPE_ORDER.indexOf(b.type_article);
                                    if (tA !== tB) return tA - tB;
                                    const cA = CARTON_NAMES.includes(a.nom.toLowerCase()) ? 0 : 1;
                                    const cB = CARTON_NAMES.includes(b.nom.toLowerCase()) ? 0 : 1;
                                    return cA - cB;
                                });
                                // Volume by type
                                const volumeByType = {};
                                mainArticles.forEach(a => { volumeByType[a.type_article] = (volumeByType[a.type_article] || 0) + a.volume * a.quantite; });

                                return (
                                    <>
                                        {/* Volume total */}
                                        <div className="flex gap-4">
                                            <div className={`w-1 rounded-full self-stretch shrink-0 ${mainArticles.length > 0 ? 'bg-emerald-500' : 'bg-red-400'}`} />
                                            <div className="flex items-center justify-between flex-1 bg-blue-50 border border-blue-600 rounded-lg px-4 py-3">
                                                <span className="text-base font-bold text-slate-700">Volume total</span>
                                                <span className="text-base font-bold text-blue-600">{articlesData.volume_total} m³</span>
                                            </div>
                                        </div>

                                        {mainArticles.length === 0 && resteArticles.length === 0 ? (
                                            <p className="text-sm text-slate-400 italic">Aucun article ajouté</p>
                                        ) : (
                                            <>
                                                {/* Main table */}
                                                {sorted.length > 0 && (
                                                    <div className="border border-slate-200 rounded overflow-hidden">
                                                        <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                                            <thead>
                                                                <tr className="bg-slate-50">
                                                                    <th className="text-left px-5 py-3 font-semibold text-slate-600 border-b border-slate-200 border-r border-r-slate-200">Article</th>
                                                                    <th className="text-center px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 border-r border-r-slate-200 w-[100px]">Qté</th>
                                                                    <th className="text-center px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 w-[120px]">Volume</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(() => {
                                                                    const rows = [];
                                                                    let lastType = null;
                                                                    sorted.forEach((a, i) => {
                                                                        const tc = TC[a.type_article] || TC.dem_1;
                                                                        const lineVol = Math.round(a.volume * a.quantite * 100) / 100;
                                                                        const isNewGroup = lastType !== null && a.type_article !== lastType;
                                                                        const isCarton = CARTON_NAMES.includes(a.nom.toLowerCase());
                                                                        rows.push(
                                                                            <tr key={i} className={tc.rowBg} style={isNewGroup ? { borderTop: '2px solid #cbd5e1' } : {}}>
                                                                                <td className="px-5 py-1.5 text-slate-800 border-b border-slate-100 border-r border-r-slate-100">{a.nom}</td>
                                                                                <td className={`text-center px-4 py-1.5 font-medium border-b border-slate-100 border-r border-r-slate-100 ${isCarton ? 'bg-emerald-100 text-emerald-700' : 'text-slate-700'}`}>{a.quantite}</td>
                                                                                <td className="text-center px-4 py-1.5 text-slate-700 border-b border-slate-100">{lineVol.toFixed(2)} m³</td>
                                                                            </tr>
                                                                        );
                                                                        lastType = a.type_article;
                                                                    });
                                                                    return rows;
                                                                })()}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Volume summary by type */}
                                                {mainArticles.length > 0 && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {TYPE_ORDER.filter(t => t !== 'reste' && (volumeByType[t] || 0) > 0).map(t => {
                                                            const vol = Math.round((volumeByType[t] || 0) * 100) / 100;
                                                            const tc = TC[t];
                                                            return (
                                                                <div key={t} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm ${tc.barBg}`}>
                                                                    <span className={`font-normal ${tc.textColor}`}>{tc.label}</span>
                                                                    <span className={`border-l border-current/20 pl-2 ${tc.textColor} font-bold`}>{vol.toFixed(2)} m³</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Articles à démonter / remonter */}
                                                {demontageArticles.length > 0 && (
                                                    <div data-testid="detail-demontage-list">
                                                        <h4 className="text-sm font-semibold text-blue-700 mb-2">Articles à démonter / remonter</h4>
                                                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                                                            <table className="w-full text-sm">
                                                                <thead>
                                                                    <tr className="bg-blue-50 border-b border-blue-100">
                                                                        <th className="text-left px-4 py-2 font-medium text-blue-700">Article</th>
                                                                        <th className="text-right px-4 py-2 font-medium text-blue-700 w-[80px]">Qté</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {demontageArticles.map((a, i) => (
                                                                        <tr key={i} className="border-b border-slate-100 last:border-0">
                                                                            <td className="px-4 py-2 text-slate-700">{a.nom}</td>
                                                                            <td className="text-right px-4 py-2 text-slate-700">{a.quantite}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Objets lourds */}
                                                {lourdArticles.length > 0 && (
                                                    <div data-testid="detail-lourd-list">
                                                        <h4 className="text-sm font-semibold text-orange-600 mb-2">Objets lourds</h4>
                                                        <div className="border border-orange-200 rounded-lg overflow-hidden">
                                                            <table className="w-full text-sm">
                                                                <thead>
                                                                    <tr className="bg-orange-50 border-b border-orange-100">
                                                                        <th className="text-left px-4 py-2 font-medium text-orange-700">Article</th>
                                                                        <th className="text-right px-4 py-2 font-medium text-orange-700 w-[80px]">Qté</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {lourdArticles.map((a, i) => (
                                                                        <tr key={i} className="border-b border-orange-50 last:border-0 bg-yellow-50/50">
                                                                            <td className="px-4 py-2 text-slate-700">{a.nom}</td>
                                                                            <td className="text-right px-4 py-2 text-slate-700">{a.quantite}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Articles à ne pas prendre */}
                                                {resteArticles.length > 0 && (
                                                    <div data-testid="detail-reste-list">
                                                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Articles à ne pas prendre</h4>
                                                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                                                            <table className="w-full text-sm">
                                                                <thead>
                                                                    <tr className="bg-gray-50 border-b border-gray-200">
                                                                        <th className="text-left px-4 py-2 font-medium text-gray-600">Article</th>
                                                                        <th className="text-right px-4 py-2 font-medium text-gray-600 w-[80px]">Qté</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {resteArticles.map((a, i) => (
                                                                        <tr key={i} className="border-b border-gray-100 last:border-0">
                                                                            <td className="px-4 py-2 text-slate-700">{a.nom}</td>
                                                                            <td className="text-right px-4 py-2 text-slate-700">{a.quantite}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </CardContent>
                    </Card>

                    {/* 10. Calcul de prix */}
                    {(() => {
                        const cp = inventaire.calcul_prix;
                        const FORMULE_LABELS = { economique: 'Économique', economique_plus: 'Éco. Plus', standard: 'Standard', luxe: 'Luxe' };
                        const FORMULE_ORDER = ['economique', 'economique_plus', 'standard', 'luxe'];
                        const fList = (inventaire.formules || []).slice().sort((a, b) => FORMULE_ORDER.indexOf(a) - FORMULE_ORDER.indexOf(b));
                        // Source unique : formule + TVA paramétrées dans Admin > Paramètres.
                        const formulaStr = priceSettings?.formula_ht || '';
                        const tvaRate = typeof priceSettings?.tva_rate === 'number' ? priceSettings.tva_rate : 20;
                        // Detection auto depuis le calculateur de volume (Section 9)
                        const _articles = articlesData?.articles || [];
                        const presenceDechetterie = _articles.some(a => (a.type_article || '') === 'decheterie' && (parseFloat(a.volume) || 0) > 0);
                        const presenceObjetLourd = _articles.some(a => a.objet_lourd === true);
                        const fd = cp?.formules_data || {};
                        const adD = inventaire.adresse_depart || {};
                        const adA = inventaire.adresse_arrivee || {};
                        const sharedKeysDetail = [];
                        if (adD.autorisation_stationnement === true) sharedKeysDetail.push('prix_aot_depart');
                        if (adA.autorisation_stationnement === true) sharedKeysDetail.push('prix_aot_arrivee');
                        if (adD.monte_meuble === true) sharedKeysDetail.push('prix_monte_meuble_depart');
                        if (adA.monte_meuble === true) sharedKeysDetail.push('prix_monte_meuble_arrivee');
                        if (presenceDechetterie) sharedKeysDetail.push('tarif_dechetterie');
                        if (presenceObjetLourd) sharedKeysDetail.push('tarif_objet_lourd');
                        // Shared values : saisies une fois et appliquées à toutes les formules
                        const sharedValues = computeSharedValues(fd, sharedKeysDetail);
                        const calcT = (d, shKeys, ib2, fourns2) => calcTotals(
                            d, shKeys, ib2, fourns2, formulaStr, tvaRate, cp?.fournitures_active,
                            { presenceDechetterie, presenceObjetLourd, sharedValues },
                        );
                        const ib = cp?.infos_base || {};
                        const fournsDetail = cp?.fournitures || {};
                        const hasData = fList.some(f => calcT(fd[f], sharedKeysDetail, ib, fournsDetail).complete);
                        // Compute max jours for dynamic effectif rows
                        const maxJours = fList.reduce((max, fKey) => {
                            const j = parseInt((fd[fKey] || {}).nb_jours) || 0;
                            return Math.max(max, j);
                        }, 0);

                        return (
                            <Card className="border-slate-200">
                                <CardHeader className="pb-2 border-b border-slate-100">
                                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                        <Calculator size={16} className="text-[#2e7cd6]" />10. Calcul de prix
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-4 space-y-10">
                                    {/* Infos de base - HIGHLIGHTED avec barre de statut globale */}
                                    <div className="flex gap-3">
                                        <div className={`w-1 rounded-full self-stretch shrink-0 transition-colors ${hasData ? 'bg-emerald-500' : 'bg-red-400'}`} />
                                        <div className="flex-1 rounded-lg border-2 border-[#2e7cd6] overflow-hidden shadow-sm ring-1 ring-[#2e7cd6]/10">
                                            <div className="bg-gradient-to-r from-[#2e7cd6] to-[#4a90d9] px-4 py-3">
                                                <h4 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                                    <Truck size={14} /> Informations de base
                                                </h4>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-blue-50/50">
                                                <div className="bg-white rounded-lg px-3 py-2 border border-[#2e7cd6]/30">
                                                    <div className="text-[10px] font-semibold text-[#2e7cd6] uppercase">Volume devis</div>
                                                    <div className="text-base font-bold text-slate-800">{ib.volume_devis || '-'} m³</div>
                                                </div>
                                                <div className="bg-white rounded-lg px-3 py-2 border border-[#2e7cd6]/30">
                                                    <div className="text-[10px] font-semibold text-[#2e7cd6] uppercase">Camion 20 m³</div>
                                                    <div className="text-base font-bold text-slate-800">{ib.camion_20 || '-'}</div>
                                                </div>
                                                <div className="bg-white rounded-lg px-3 py-2 border border-[#2e7cd6]/30">
                                                    <div className="text-[10px] font-semibold text-[#2e7cd6] uppercase">Camion 30 m³</div>
                                                    <div className="text-base font-bold text-slate-800">{ib.camion_30 || '-'}</div>
                                                </div>
                                                <div className="bg-white rounded-lg px-3 py-2 border border-[#2e7cd6]/30">
                                                    <div className="text-[10px] font-semibold text-[#2e7cd6] uppercase">Distance</div>
                                                    <div className="text-base font-bold text-slate-800">{ib.distance ? `${ib.distance} km` : '-'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Fournitures */}
                                    {cp?.fournitures_active === true && (() => {
                                        const fn = cp?.fournitures || {};
                                        const allFilled = !!fn.cartons_standards && !!fn.cartons_livres && !!fn.cartons_verres && !!fn.cartons_bouteilles && !!fn.adhesif && !!fn.bulle_grande && !!fn.bulle_petite && !!fn.autres;
                                        return (
                                        <div>
                                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                <Package size={12} /> Fournitures (Oui)
                                                <SectionCheck isComplete={allFilled} />
                                            </h4>
                                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                                {[{ k: 'cartons_standards', l: 'Cartons std' }, { k: 'cartons_livres', l: 'Cartons livres' }, { k: 'cartons_verres', l: 'Cartons verres (100v)' }, { k: 'cartons_bouteilles', l: 'Cartons bout.' }, { k: 'adhesif', l: 'Adhésif' }, { k: 'bulle_grande', l: 'Bulle 100m x 50cm' }, { k: 'bulle_petite', l: 'Bulle 10m x 1m' }, { k: 'autres', l: 'Autre fourniture' }].map(f => (
                                                    <div key={f.k} className="bg-slate-50 rounded-lg px-3 py-2">
                                                        <div className="text-[10px] text-slate-500 uppercase">{f.l}</div>
                                                        <div className="text-sm font-semibold text-slate-700">{fn[f.k] || '-'}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        );
                                    })()}

                                    {/* Fournitures = Non : afficher uniquement "Autre fourniture" */}
                                    {cp?.fournitures_active === false && (() => {
                                        const fn = cp?.fournitures || {};
                                        const autresFilled = fn.autres !== '' && fn.autres !== null && fn.autres !== undefined;
                                        return (
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                    <Package size={12} /> Fournitures (Non)
                                                    <SectionCheck isComplete={autresFilled} />
                                                </h4>
                                                <div className="bg-slate-50 rounded-lg px-3 py-2 inline-block min-w-[160px]">
                                                    <div className="text-[10px] text-slate-500 uppercase">Autre fourniture</div>
                                                    <div className="text-sm font-semibold text-slate-700">{fn.autres || '-'}</div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Statistiques exploitation */}
                                    {(() => {
                                        const rawDate = inventaire.adresse_depart?.date || '';
                                        if (!rawDate) return null;
                                        const startDate = new Date(rawDate);
                                        if (isNaN(startDate.getTime())) return null;
                                        const maxJ = Math.max(1, ...fList.map(fk => parseInt((fd[fk] || {}).nb_jours) || 0));
                                        const rows = [];
                                        for (let i = 0; i < maxJ; i++) {
                                            const d = new Date(startDate.getTime() + i * 86400000);
                                            const dateKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                                            const dateDisplay = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
                                            const stats = programmationStats[dateKey] || {};
                                            rows.push({ jour: i + 1, date: dateDisplay, dem: stats.nb_demenageurs || 0, c20: stats.camion_20m3 || 0, c30: stats.camion_30m3 || 0 });
                                        }
                                        return (
                                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="stats-exploitation-detail">
                                                <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
                                                    <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                                                        <CalendarDays size={14} className="text-[#2e7cd6]" /> Statistiques exploitation
                                                    </h4>
                                                    <p className="text-[10px] text-slate-400 mt-1">Ressources déjà programmées sur ces dates (tous clients confondus)</p>
                                                </div>
                                                <div className="p-4">
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm border border-slate-200 rounded-lg">
                                                            <thead>
                                                                <tr className="bg-slate-50 border-b border-slate-200">
                                                                    <th className="text-left px-4 py-2 text-xs font-bold text-slate-500 uppercase">Jour</th>
                                                                    <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Date</th>
                                                                    <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Déménageurs</th>
                                                                    <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Camion 20m³</th>
                                                                    <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Camion 30m³</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {rows.map((r, i) => {
                                                                    const hasBookings = r.dem > 0 || r.c20 > 0 || r.c30 > 0;
                                                                    return (
                                                                        <tr key={i} className={hasBookings ? 'bg-amber-50/50' : 'bg-white'}>
                                                                            <td className="px-4 py-2 font-medium text-slate-700 border-b border-slate-100">Jour {r.jour}</td>
                                                                            <td className="px-4 py-2 text-center text-slate-700 border-b border-slate-100">{r.date}</td>
                                                                            <td className={`px-4 py-2 text-center font-semibold border-b border-slate-100 ${r.dem > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{r.dem}</td>
                                                                            <td className={`px-4 py-2 text-center font-semibold border-b border-slate-100 ${r.c20 > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{r.c20}</td>
                                                                            <td className={`px-4 py-2 text-center font-semibold border-b border-slate-100 ${r.c30 > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{r.c30}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Tableau couts */}
                                    {fList.length > 0 && hasData && (
                                        <div className="overflow-x-auto border border-slate-200 rounded-lg">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b border-slate-200 bg-slate-50">
                                                        <th className="text-left px-4 py-2 text-xs font-bold text-slate-500 uppercase w-[160px]">Ligne</th>
                                                        {fList.map(fKey => (
                                                            <th key={fKey} className="text-center px-3 py-2 text-xs font-bold text-blue-600 uppercase">{FORMULE_LABELS[fKey] || fKey}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* Nombre de jours */}
                                                    <tr className="bg-white">
                                                        <td className="px-4 py-2 text-xs font-medium text-slate-600 border-r border-slate-100">Nombre de jours</td>
                                                        {fList.map(fKey => (
                                                            <td key={fKey} className="px-3 py-2 text-center text-sm text-slate-700">{(fd[fKey] || {}).nb_jours || '-'}</td>
                                                        ))}
                                                    </tr>
                                                    {/* Dynamic Effectif jr X rows */}
                                                    {Array.from({ length: maxJours }, (_, idx) => idx + 1).map(dayNum => (
                                                        <tr key={`eff-${dayNum}`} className={dayNum % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                                                            <td className="px-4 py-2 text-xs font-medium text-blue-600 border-r border-slate-100">Effectif jr {dayNum}</td>
                                                            {fList.map(fKey => {
                                                                const fData = fd[fKey] || {};
                                                                const nbJ = parseInt(fData.nb_jours) || 0;
                                                                const ej = fData.effectif_jours || {};
                                                                return (
                                                                    <td key={fKey} className="px-3 py-2 text-center text-sm text-slate-700">
                                                                        {dayNum <= nbJ ? (ej[String(dayNum)] || '-') : <span className="text-slate-300">-</span>}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                    {/* Tarif, Panier, Hotel */}
                                                    {[{ k: 'tarif_demenageur', l: 'Tarif déménageur' }, { k: 'panier', l: 'Panier' }, { k: 'hotel', l: 'Hôtel' }].map((row, i) => (
                                                        <tr key={row.k} className={(maxJours + 1 + i) % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                                            <td className="px-4 py-2 text-xs font-medium text-slate-600 border-r border-slate-100">{row.l}</td>
                                                            {fList.map(fKey => (
                                                                <td key={fKey} className="px-3 py-2 text-center text-sm text-slate-700">{(fd[fKey] || {})[row.k] || '-'}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                    {/* Dynamic AOT / Monte-meuble rows (valeur partagée, affichée en colspan) */}
                                                    {(() => {
                                                        const adD = inventaire.adresse_depart || {};
                                                        const adA = inventaire.adresse_arrivee || {};
                                                        const dynRows = [];
                                                        if (adD.autorisation_stationnement === true) dynRows.push({ k: 'prix_aot_depart', l: 'Prix AOT départ' });
                                                        if (adA.autorisation_stationnement === true) dynRows.push({ k: 'prix_aot_arrivee', l: 'Prix AOT arrivée' });
                                                        if (adD.monte_meuble === true) dynRows.push({ k: 'prix_monte_meuble_depart', l: 'Prix Monte-meuble départ' });
                                                        if (adA.monte_meuble === true) dynRows.push({ k: 'prix_monte_meuble_arrivee', l: 'Prix Monte-meuble arrivée' });
                                                        return dynRows.map((row, i) => (
                                                            <tr key={row.k} className={(maxJours + 4 + i) % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'}>
                                                                <td className="px-4 py-2 text-xs font-medium text-amber-700 border-r border-slate-100">{row.l}</td>
                                                                <td colSpan={fList.length} className="px-3 py-2 text-center text-sm text-slate-700">
                                                                    {sharedValues[row.k] || '-'}
                                                                </td>
                                                            </tr>
                                                        ));
                                                    })()}
                                                    {/* Tarif déchetterie / objet lourd (valeur partagée, affichée en colspan) */}
                                                    {(() => {
                                                        const extraRows = [];
                                                        if (presenceDechetterie) extraRows.push({ k: 'tarif_dechetterie', l: 'Tarif déchetterie' });
                                                        if (presenceObjetLourd) extraRows.push({ k: 'tarif_objet_lourd', l: 'Tarif objet lourd' });
                                                        return extraRows.map((row) => (
                                                            <tr key={row.k} className="bg-orange-50/40">
                                                                <td className="px-4 py-2 text-xs font-medium text-orange-700 border-r border-slate-100">{row.l}</td>
                                                                <td colSpan={fList.length} className="px-3 py-2 text-center text-sm text-slate-700">
                                                                    {sharedValues[row.k] || '-'}
                                                                </td>
                                                            </tr>
                                                        ));
                                                    })()}
                                                    {/* Marge */}
                                                    <tr className="bg-emerald-50/60 border-t border-emerald-200">
                                                        <td className="px-4 py-2.5 text-xs font-bold text-emerald-700 border-r border-emerald-200">Marge</td>
                                                        {fList.map(fKey => (
                                                            <td key={fKey} className="px-3 py-2 text-center text-sm font-semibold text-emerald-700">{(fd[fKey] || {}).marge || '-'}</td>
                                                        ))}
                                                    </tr>
                                                    <tr className="border-t-2 border-slate-300 bg-blue-50/50">
                                                        <td className="px-4 py-2 text-xs font-bold text-slate-700 border-r border-slate-200">Total HT</td>
                                                        {fList.map(fKey => (
                                                            <td key={fKey} className="px-3 py-2 text-center font-bold text-slate-700">{calcT(fd[fKey], sharedKeysDetail, ib, fournsDetail).ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                                                        ))}
                                                    </tr>
                                                    <tr className="bg-blue-100/50">
                                                        <td className="px-4 py-2 text-xs font-bold text-blue-700 border-r border-slate-200">Total TTC</td>
                                                        {fList.map(fKey => (
                                                            <td key={fKey} className="px-3 py-2 text-center font-extrabold text-blue-700 text-base">{calcT(fd[fKey], sharedKeysDetail, ib, fournsDetail).ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                                                        ))}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })()}

                </div>
            </div>

            </ReadOnlyGuard>

            {/* PDF Preview Modal — rendu hors du ReadOnlyGuard pour que les boutons
                Fermer / Télécharger restent cliquables, même en mode lecture seule */}
            {pdfPreviewOpen && (
                <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4" data-testid="inventaire-pdf-preview-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <FileText size={18} className="text-[#2e7cd6]" />
                                Aperçu PDF — Inventaire
                            </h2>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPdfPreviewOpen(false)}
                                    data-testid="inventaire-pdf-close-btn"
                                >
                                    Fermer
                                </Button>
                                <Button
                                    size="sm"
                                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                    disabled={!pdfBlob}
                                    onClick={() => {
                                        if (!pdfBlob) return;
                                        const filename = `inventaire_${(inventaire.client_nom || inventaire.id).toString().replace(/[^\w\-. ]/g, '_')}.pdf`;
                                        // Fallback IE / vieux Edge
                                        if (window.navigator && window.navigator.msSaveBlob) {
                                            window.navigator.msSaveBlob(pdfBlob, filename);
                                            return;
                                        }
                                        // On genere une URL FRESH depuis le Blob (le pdfUrl utilise dans
                                        // l'iframe peut avoir ete revoque par le navigateur sur mobile
                                        // ou apres un rerender -> declenchait une page blanche).
                                        const freshUrl = URL.createObjectURL(pdfBlob);
                                        const a = document.createElement('a');
                                        a.href = freshUrl;
                                        a.download = filename;
                                        a.target = '_self';
                                        a.rel = 'noopener';
                                        // "hidden" evite que Firefox ne rende visible l'element
                                        a.style.display = 'none';
                                        document.body.appendChild(a);
                                        a.click();
                                        // Cleanup differe : Safari a besoin que l'URL survive au click().
                                        setTimeout(() => {
                                            document.body.removeChild(a);
                                            URL.revokeObjectURL(freshUrl);
                                        }, 200);
                                    }}
                                    data-testid="inventaire-pdf-download-btn"
                                >
                                    <Download size={14} className="mr-1.5" />
                                    Télécharger
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 bg-slate-100">
                            {pdfUrl ? (
                                <iframe
                                    title="Aperçu PDF Inventaire"
                                    src={pdfUrl}
                                    className="w-full h-full border-0"
                                    data-testid="inventaire-pdf-iframe"
                                />
                            ) : (
                                <div className="flex items-center justify-center h-full text-slate-500">
                                    <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 border-2 border-[#2e7cd6] border-t-transparent rounded-full animate-spin" />
                                        Chargement du PDF...
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
